<template>
  <component
    :is="as"
    :type="nativeType"
    :disabled="disabled || loading"
    :class="buttonClasses"
    v-bind="$attrs"
  >
    <div class="flex items-center gap-1">
      <LoaderIcon v-if="loading" class="w-5 h-5 animate-spin me-2" />
      <slot></slot>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { LoaderIcon } from "lucide-vue-next"
const props = withDefaults(
  defineProps<{
    as?: string;
    nativeType?: string;
    variant?:
      | "primary"
      | "secondary"
      | "danger"
      | "ghost"
      | "brown"
      | "outline";
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    disabled?: boolean;
    loading?: boolean;
    fullWidth?: boolean;
    iconOnly?: boolean;
  }>(),
  {
    as: "button",
    nativeType: "button",
    variant: "primary",
    size: "md",
    disabled: false,
    loading: false,
    fullWidth: false,
    iconOnly: false,
  }
)

const variantClasses = {
  primary: "bg-primary-500 text-white hover:bg-blue-700",
  secondary: "bg-gray-600 text-white hover:bg-gray-700",
  danger: "bg-red-600 text-white hover:bg-red-700",
  ghost:
    "bg-transparent text-gray-700 border border-gray-300 hover:bg-gray-100",
  brown:
    "bg-secondary-800 text-white hover:bg-secondary-900 focus:outline outline-offset-4 outline-secondary-800",
  outline:
    "border border-primary-400 text-primary-400 hover:bg-white-100 hover:text-white hover:bg-primary-400",
}

const sizeClasses = {
  xs: "text-xs px-2 py-1",
  sm: "text-sm px-3 py-1.5",
  md: "text-base px-4 py-2",
  lg: "text-lg px-5 py-2.5",
  xl: "text-xl px-6 py-3",
}

const buttonClasses = computed(() => [
  "inline-flex items-center justify-center font-medium rounded-md transition-colors duration-200",
  variantClasses[props.variant],
  sizeClasses[props.size],
  {
    "w-full": props.fullWidth,
    "p-2": props.iconOnly,
    "opacity-50 cursor-not-allowed": props.disabled || props.loading,
  },
])
</script>
